import type { GetCatalogProductDetailPageOption_pdp_option_info as OptionInfo } from '../graphql/select-option_types.2';
import { PdpDeliveryType } from '../graphql/select-option_types.2';
import type { MatchingProductOptionList, ProductOptionList, ShippingTypeIds } from '../type/common.type';
import type { OptionData } from '../type/common.type';
import {
  checkZigzinOrVendorFlexDeliveryType,
  checkZigzinOrVendorFlexFulfillmentType,
} from '../util/checkers/zigzin-checker';
import { parseShippingType } from '../util/transformers/shipping-type-transformer';

/**
 * 배송 타입별 옵션 ID 찾기
 * @param option - 옵션 데이터
 * @param index - 옵션 인덱스
 * @returns 배송 타입 ID
 */
export const getOptionShippingTypeIds = (option_info: OptionInfo | null, option: OptionData, index: number) => {
  if (!option_info) {
    return { zigzin_id: '', general_id: '' };
  }
  const is_zigzin_fulfillment = checkZigzinOrVendorFlexFulfillmentType(option_info?.catalog_product?.fulfillment_type);

  return findShippingTypeIds(option, {
    current_option_index: index,
    shipping_type: is_zigzin_fulfillment ? PdpDeliveryType.ZIGZIN : PdpDeliveryType.GENERAL,
    product_option_list: option_info.catalog_product.product_option_list ?? [],
    matching_option_list: option_info.catalog_product.matching_catalog_product_info?.option_list ?? [],
  });
};

/**
 * 선택된 옵션의 매칭 옵션 ID를 찾습니다.
 */

export interface ShippingMatchContext {
  /** 현재 옵션 레벨 인덱스 */
  current_option_index: number;
  /** 진입한 상품의 배송 타입 */
  shipping_type: PdpDeliveryType;
  /** 제품 옵션 리스트 */
  product_option_list: ProductOptionList[];
  /** 매칭 카탈로그 옵션 리스트 */
  matching_option_list: MatchingProductOptionList[];
}

const findShippingTypeIds = (selected_option: OptionData, context: ShippingMatchContext): ShippingTypeIds => {
  const is_zigzin_option_selected = checkZigzinOrVendorFlexDeliveryType(selected_option.shipping?.type ?? null);

  // 선택된 옵션의 ID를 우선 할당
  const zigzin_id = is_zigzin_option_selected ? selected_option.id : null;
  const general_id = is_zigzin_option_selected ? null : selected_option.id;

  /**
   * option_list_for_search: 검색할 옵션 리스트 결정
   *
   * 배송 타입에 따라 다른 옵션 리스트에서 매칭 옵션을 찾아야 함:
   * - 진입 상품의 배송 타입 === 선택된 옵션의 배송 타입인 경우:
   *   같은 배송 타입이므로 매칭 카탈로그 옵션 리스트(matching_option_list)에서
   *   다른 배송 타입의 매칭 옵션을 찾음
   *
   * - 진입 상품의 배송 타입 !== 선택된 옵션의 배송 타입인 경우:
   *   다른 배송 타입이므로 메인 제품 옵션 리스트(product_option_list)에서
   *   같은 이름을 가진 옵션을 찾음
   *
   * 예시:
   * - 진입: 직진배송, 선택: 직진배송 → matching_option_list에서 일반배송 옵션 찾기
   * - 진입: 직진배송, 선택: 일반배송 → product_option_list에서 같은 이름의 옵션 찾기
   */
  const option_list_for_search =
    parseShippingType(context.shipping_type) === parseShippingType(selected_option.shipping?.type || null)
      ? context.matching_option_list
      : context.product_option_list;

  // 매칭되는 옵션 ID 찾기
  const matching_id = findMatchingOptionId(selected_option.name, {
    current_option_index: context.current_option_index,
    option_list: option_list_for_search,
  });

  return {
    zigzin_id: zigzin_id ?? matching_id,
    general_id: general_id ?? matching_id,
  };
};

/**
 * 매칭되는 옵션 ID를 찾습니다.
 * option_list의 value_list에서 같은 value를 가진 다른 배송 타입 옵션을 검색합니다.
 */
interface ShippingMatchContextForSearch {
  /** 현재 옵션 레벨 인덱스 */
  current_option_index: number;
  /** 제품 옵션 리스트 (일반 또는 매칭) */
  option_list: ProductOptionList[] | MatchingProductOptionList[];
}

const findMatchingOptionId = (selected_name: string, context: ShippingMatchContextForSearch): string | null => {
  const { current_option_index, option_list } = context;

  if (option_list && current_option_index < option_list.length) {
    const current_option = option_list[current_option_index];

    if (current_option?.value_list) {
      for (const value_item of current_option.value_list) {
        if (value_item.value === selected_name) {
          return value_item.id;
        }
      }
    }
  }
  return null;
};
